import { useState } from "react";
import { useAuth } from "@/components/AuthProvider";
import { Navigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Phone, Clock, Users, Activity, MessageSquare, TrendingUp } from "lucide-react";
import { useVoiceAgentOverview, useVoiceAgentDailyStats, useVoiceAgentSessions } from "@/hooks/useVoiceAgentAnalytics";
import VoiceAgentStatsCards from "@/components/admin/VoiceAgentStatsCards";
import VoiceAgentSessionsTable from "@/components/admin/VoiceAgentSessionsTable";
import VoiceAgentUsageChart from "@/components/admin/VoiceAgentUsageChart";
import VoiceAgentSessionDetails from "@/components/admin/VoiceAgentSessionDetails";

const VoiceAgentDashboard = () => {
  const { user, loading } = useAuth();
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  
  const { data: overview, isLoading: overviewLoading } = useVoiceAgentOverview();
  const { data: dailyStats, isLoading: statsLoading } = useVoiceAgentDailyStats(30);
  const { data: sessions, isLoading: sessionsLoading } = useVoiceAgentSessions(100);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Voice Agent Analytics</h1>
          <p className="text-muted-foreground mt-2">
            Monitor voice agent conversations, usage patterns, and performance metrics
          </p>
        </div>

        <VoiceAgentStatsCards 
          overview={overview} 
          isLoading={overviewLoading} 
          formatDuration={formatDuration}
        />

        <Tabs defaultValue="overview" className="mt-8">
          <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="sessions">Sessions</TabsTrigger>
            <TabsTrigger value="details">Details</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Usage Trends (Last 30 Days)
                </CardTitle>
                <CardDescription>
                  Daily voice agent session and message counts
                </CardDescription>
              </CardHeader>
              <CardContent>
                <VoiceAgentUsageChart 
                  dailyStats={dailyStats || []} 
                  isLoading={statsLoading} 
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sessions" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  Recent Sessions
                </CardTitle>
                <CardDescription>
                  View all voice agent conversation sessions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <VoiceAgentSessionsTable 
                  sessions={sessions || []} 
                  isLoading={sessionsLoading}
                  onSelectSession={setSelectedSessionId}
                  selectedSessionId={selectedSessionId}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="details" className="mt-6">
            <VoiceAgentSessionDetails 
              sessionId={selectedSessionId}
              onClose={() => setSelectedSessionId(null)}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default VoiceAgentDashboard;
